/**
 * AudioSystem - audio 100% sintetizado em Web Audio. NENHUM arquivo de som.
 *
 * Como o som e feito aqui:
 *   - ruido branco/rosa/marrom em AudioBuffer gerado uma unica vez no init
 *   - camadas curtas (transiente, corpo, crack, mecanica, cauda) somadas numa voz
 *   - envelope por AudioParam (linear na subida, exponencial na queda)
 *   - BiquadFilter para timbre e para abafamento por distancia/parede
 *   - WaveShaper (tanh) para sujeira/saturacao do tiro
 *   - ConvolverNode com impulso gerado em codigo (rua estreita, cauda curta)
 *
 * Cadeia de barramentos:
 *
 *   vozes posicionais -> panner -+-> sfxBus  -> duck ------+
 *   vozes centrais ---------------+                       |
 *   ambiente ---------------------> ambientBus -> duck ---+--> masterFilter -> limiter -> master -> saida
 *   tiro do jogador --------------> shotBus -> softclip --+
 *   envio de reverb --------------> reverbBus -> convolver -> return ---------+
 *
 * O tiro do jogador NAO passa pelo duck (senao ele abaixaria a si mesmo): ele
 * abaixa todo o resto por ~80 ms, que e o que da a sensacao de "soco".
 *
 * Regras respeitadas: zero alocacao por frame no update; nada lanca excecao no
 * laco (tudo protegido, aviso uma unica vez); nomes de superficie vem de
 * Constants.SURFACES; volumes vem de settings.
 */

import * as THREE from 'three';
import { SURFACES, PLAYER } from '../core/Constants.js';

// --- temporarios de modulo (nunca alocar dentro de update/fixedUpdate) ------
const _tmpPos = new THREE.Vector3();
const _tmpFwd = new THREE.Vector3();
const _tmpUp = new THREE.Vector3();
const _tmpA = new THREE.Vector3();
const _tmpB = new THREE.Vector3();

const MAX_VOICES = 24;
const MAX_SCHEDULED = 40;
const SOUND_SPEED = 343;          // m/s - atraso de chegada do som distante
const BULLET_SPEED = 820;         // m/s - usado so para o assobio da bala
const MAX_TRAVEL_DELAY = 0.42;    // s - teto do atraso, para nao soar desconexo
const LOS_PER_FRAME = 8;          // orcamento de testes de parede por frame

/**
 * Parametros da proxima camada de som. Objeto UNICO reutilizado: preencher com
 * Lreset() + campos e chamar _spawnNoise/_spawnTone imediatamente (o helper le
 * tudo de forma sincrona). Existe para nao alocar um literal por camada.
 */
const L = {
  buf: null,          // AudioBuffer de ruido (so em _spawnNoise)
  rate: 1,            // playbackRate do ruido (varia o "grao")
  wave: 'sine',       // forma de onda (so em _spawnTone)
  f0: 1000,           // ruido: corte inicial | tom: frequencia inicial
  f1: 0,              // destino da varredura (0 = sem varredura)
  cut: 0,             // tom: corte do filtro (0 = sem filtro)
  type: 'bandpass',   // tipo do biquad ('none' desliga)
  q: 1,
  peak: 0.5,
  attack: 0.001,
  decay: 0.1,
  delay: 0,
  drive: 0            // >0 aplica waveshaper (>4 usa a curva dura)
};

function Lreset() {
  L.buf = null; L.rate = 1; L.wave = 'sine';
  L.f0 = 1000; L.f1 = 0; L.cut = 0; L.type = 'bandpass'; L.q = 1;
  L.peak = 0.5; L.attack = 0.001; L.decay = 0.1; L.delay = 0; L.drive = 0;
}

/**
 * Timbre por familia de arma. O que separa uma da outra:
 *   fuzil    - grave, seco, cauda media
 *   smg      - agudo, curto, cadencia alta (cauda curta para nao embolar)
 *   dmr      - estourado, sub forte, cauda longa
 *   shotgun  - estouro largo e grave, sem crack agudo
 */
const SHOT_PROFILES = {
  rifle: {
    gain: 1.00, jitter: 0.055, drive: 6,
    clickPeak: 0.50, clickDecay: 0.013, clickF: 2700,
    bodyF0: 1500, bodyF1: 195, bodyQ: 1.1, bodyDecay: 0.115, bodyPeak: 0.80,
    subF0: 168, subF1: 46, subDecay: 0.105, subPeak: 0.50,
    crackF: 4300, crackQ: 1.1, crackDecay: 0.048, crackPeak: 0.38,
    mechF: 2700, mechDelay: 0.028, mechPeak: 0.11,
    tailF0: 760, tailF1: 320, tailDecay: 0.40, tailPeak: 0.15, send: 0.40
  },
  smg: {
    gain: 0.84, jitter: 0.075, drive: 5,
    clickPeak: 0.46, clickDecay: 0.010, clickF: 3400,
    bodyF0: 2000, bodyF1: 280, bodyQ: 1.0, bodyDecay: 0.072, bodyPeak: 0.64,
    subF0: 210, subF1: 72, subDecay: 0.070, subPeak: 0.32,
    crackF: 5300, crackQ: 1.3, crackDecay: 0.034, crackPeak: 0.42,
    mechF: 3300, mechDelay: 0.020, mechPeak: 0.14,
    tailF0: 980, tailF1: 460, tailDecay: 0.24, tailPeak: 0.11, send: 0.30
  },
  dmr: {
    gain: 1.18, jitter: 0.040, drive: 7,
    clickPeak: 0.60, clickDecay: 0.016, clickF: 2400,
    bodyF0: 1250, bodyF1: 145, bodyQ: 1.2, bodyDecay: 0.165, bodyPeak: 0.92,
    subF0: 132, subF1: 38, subDecay: 0.150, subPeak: 0.62,
    crackF: 3500, crackQ: 0.9, crackDecay: 0.062, crackPeak: 0.52,
    mechF: 2300, mechDelay: 0.040, mechPeak: 0.13,
    tailF0: 620, tailF1: 240, tailDecay: 0.82, tailPeak: 0.20, send: 0.62
  },
  shotgun: {
    gain: 1.22, jitter: 0.050, drive: 7,
    clickPeak: 0.34, clickDecay: 0.018, clickF: 1900,
    bodyF0: 950, bodyF1: 115, bodyQ: 0.9, bodyDecay: 0.195, bodyPeak: 0.98,
    subF0: 112, subF1: 34, subDecay: 0.180, subPeak: 0.66,
    crackF: 2500, crackQ: 0.8, crackDecay: 0.055, crackPeak: 0.26,
    mechF: 1900, mechDelay: 0.055, mechPeak: 0.16,
    tailF0: 560, tailF1: 230, tailDecay: 0.58, tailPeak: 0.18, send: 0.52
  }
};

/**
 * Impacto por superficie. res* e a ressonancia (o "ping" do metal, o "toc" da
 * madeira); grains sao os cacos/lascas que caem logo depois do impacto.
 */
const IMPACT_PROFILES = {
  concrete:  { gain: 1.00, bodyF: 1150, bodyQ: 0.9, bodyDecay: 0.090, bodyPeak: 0.72, clickF: 3600, clickDecay: 0.012, clickPeak: 0.46, grains: 3, grainF: 2600, grainDecay: 0.040, grainPeak: 0.15, resF: 0, resQ: 0, resDecay: 0, resPeak: 0, resTone: false, drive: 3, send: 0.26 },
  asphalt:   { gain: 0.92, bodyF: 820,  bodyQ: 0.8, bodyDecay: 0.085, bodyPeak: 0.70, clickF: 2700, clickDecay: 0.010, clickPeak: 0.34, grains: 2, grainF: 1800, grainDecay: 0.045, grainPeak: 0.12, resF: 0, resQ: 0, resDecay: 0, resPeak: 0, resTone: false, drive: 3, send: 0.22 },
  brick:     { gain: 1.02, bodyF: 1320, bodyQ: 1.0, bodyDecay: 0.085, bodyPeak: 0.72, clickF: 4100, clickDecay: 0.013, clickPeak: 0.48, grains: 4, grainF: 3000, grainDecay: 0.045, grainPeak: 0.16, resF: 0, resQ: 0, resDecay: 0, resPeak: 0, resTone: false, drive: 3, send: 0.26 },
  plaster:   { gain: 0.86, bodyF: 900,  bodyQ: 0.7, bodyDecay: 0.075, bodyPeak: 0.62, clickF: 3000, clickDecay: 0.010, clickPeak: 0.30, grains: 3, grainF: 2200, grainDecay: 0.055, grainPeak: 0.13, resF: 0, resQ: 0, resDecay: 0, resPeak: 0, resTone: false, drive: 2, send: 0.24 },
  tile:      { gain: 0.98, bodyF: 1500, bodyQ: 1.1, bodyDecay: 0.065, bodyPeak: 0.60, clickF: 5200, clickDecay: 0.013, clickPeak: 0.50, grains: 4, grainF: 4200, grainDecay: 0.050, grainPeak: 0.17, resF: 2400, resQ: 18, resDecay: 0.130, resPeak: 0.20, resTone: false, drive: 2, send: 0.32 },
  metal:     { gain: 1.05, bodyF: 2000, bodyQ: 0.9, bodyDecay: 0.040, bodyPeak: 0.52, clickF: 5000, clickDecay: 0.011, clickPeak: 0.52, grains: 1, grainF: 3400, grainDecay: 0.040, grainPeak: 0.10, resF: 640,  resQ: 22, resDecay: 0.360, resPeak: 0.34, resTone: true,  drive: 4, send: 0.40 },
  metal_thin:{ gain: 1.00, bodyF: 2600, bodyQ: 0.9, bodyDecay: 0.032, bodyPeak: 0.44, clickF: 6200, clickDecay: 0.010, clickPeak: 0.50, grains: 1, grainF: 4600, grainDecay: 0.035, grainPeak: 0.10, resF: 1450, resQ: 28, resDecay: 0.280, resPeak: 0.32, resTone: true,  drive: 4, send: 0.42 },
  wood:      { gain: 0.96, bodyF: 900,  bodyQ: 0.8, bodyDecay: 0.070, bodyPeak: 0.66, clickF: 2600, clickDecay: 0.011, clickPeak: 0.36, grains: 2, grainF: 1900, grainDecay: 0.045, grainPeak: 0.13, resF: 320,  resQ: 9,  resDecay: 0.130, resPeak: 0.26, resTone: true,  drive: 3, send: 0.24 },
  glass:     { gain: 1.00, bodyF: 3200, bodyQ: 0.8, bodyDecay: 0.045, bodyPeak: 0.46, clickF: 7000, clickDecay: 0.009, clickPeak: 0.52, grains: 7, grainF: 6400, grainDecay: 0.090, grainPeak: 0.20, resF: 3800, resQ: 20, resDecay: 0.240, resPeak: 0.22, resTone: true,  drive: 2, send: 0.38 },
  dirt:      { gain: 0.84, bodyF: 560,  bodyQ: 0.7, bodyDecay: 0.100, bodyPeak: 0.66, clickF: 1600, clickDecay: 0.010, clickPeak: 0.18, grains: 3, grainF: 1300, grainDecay: 0.060, grainPeak: 0.11, resF: 0, resQ: 0, resDecay: 0, resPeak: 0, resTone: false, drive: 2, send: 0.16 },
  sand:      { gain: 0.78, bodyF: 700,  bodyQ: 0.6, bodyDecay: 0.070, bodyPeak: 0.56, clickF: 2400, clickDecay: 0.012, clickPeak: 0.16, grains: 4, grainF: 3600, grainDecay: 0.075, grainPeak: 0.13, resF: 0, resQ: 0, resDecay: 0, resPeak: 0, resTone: false, drive: 1, send: 0.14 },
  gravel:    { gain: 0.90, bodyF: 820,  bodyQ: 0.7, bodyDecay: 0.080, bodyPeak: 0.60, clickF: 2900, clickDecay: 0.011, clickPeak: 0.28, grains: 6, grainF: 2800, grainDecay: 0.070, grainPeak: 0.16, resF: 0, resQ: 0, resDecay: 0, resPeak: 0, resTone: false, drive: 2, send: 0.20 },
  grass:     { gain: 0.70, bodyF: 620,  bodyQ: 0.6, bodyDecay: 0.075, bodyPeak: 0.46, clickF: 3200, clickDecay: 0.014, clickPeak: 0.14, grains: 4, grainF: 4000, grainDecay: 0.080, grainPeak: 0.11, resF: 0, resQ: 0, resDecay: 0, resPeak: 0, resTone: false, drive: 1, send: 0.14 },
  fabric:    { gain: 0.68, bodyF: 700,  bodyQ: 0.6, bodyDecay: 0.065, bodyPeak: 0.50, clickF: 2200, clickDecay: 0.010, clickPeak: 0.12, grains: 1, grainF: 2000, grainDecay: 0.050, grainPeak: 0.07, resF: 0, resQ: 0, resDecay: 0, resPeak: 0, resTone: false, drive: 1, send: 0.12 },
  rubber:    { gain: 0.80, bodyF: 430,  bodyQ: 0.8, bodyDecay: 0.085, bodyPeak: 0.60, clickF: 1500, clickDecay: 0.010, clickPeak: 0.16, grains: 0, grainF: 1200, grainDecay: 0.040, grainPeak: 0.06, resF: 180,  resQ: 6,  resDecay: 0.090, resPeak: 0.18, resTone: true,  drive: 2, send: 0.14 },
  sandbag:   { gain: 0.76, bodyF: 600,  bodyQ: 0.6, bodyDecay: 0.085, bodyPeak: 0.58, clickF: 2000, clickDecay: 0.010, clickPeak: 0.14, grains: 4, grainF: 3200, grainDecay: 0.085, grainPeak: 0.13, resF: 0, resQ: 0, resDecay: 0, resPeak: 0, resTone: false, drive: 1, send: 0.12 },
  water:     { gain: 0.86, bodyF: 1800, bodyQ: 0.6, bodyDecay: 0.110, bodyPeak: 0.60, clickF: 4200, clickDecay: 0.020, clickPeak: 0.26, grains: 5, grainF: 5200, grainDecay: 0.110, grainPeak: 0.15, resF: 0, resQ: 0, resDecay: 0, resPeak: 0, resTone: false, drive: 1, send: 0.30 },
  flesh:     { gain: 0.94, bodyF: 500,  bodyQ: 0.9, bodyDecay: 0.085, bodyPeak: 0.72, clickF: 1400, clickDecay: 0.014, clickPeak: 0.22, grains: 2, grainF: 900,  grainDecay: 0.060, grainPeak: 0.16, resF: 0, resQ: 0, resDecay: 0, resPeak: 0, resTone: false, drive: 3, send: 0.10 }
};

/**
 * Passo por superficie. thump e o peso do corpo; scuff e o atrito da bota;
 * grains sao pedras/areia soltas; gear e o tinido do equipamento (o detalhe
 * que faz o passo soar "soldado" e nao "pessoa de chinelo").
 */
const STEP_PROFILES = {
  concrete:  { gain: 1.00, thumpF: 95,  thumpDecay: 0.055, thumpPeak: 0.40, scuffF: 2300, scuffQ: 0.8, scuffDecay: 0.055, scuffPeak: 0.30, grains: 0, grainF: 2600, grainDecay: 0.040, grainPeak: 0.10, resF: 0,   resQ: 0,  resDecay: 0,     resPeak: 0,    gear: 0.16, lp: 11000 },
  asphalt:   { gain: 0.94, thumpF: 88,  thumpDecay: 0.055, thumpPeak: 0.38, scuffF: 1800, scuffQ: 0.8, scuffDecay: 0.060, scuffPeak: 0.26, grains: 2, grainF: 2100, grainDecay: 0.045, grainPeak: 0.09, resF: 0,   resQ: 0,  resDecay: 0,     resPeak: 0,    gear: 0.15, lp: 9000 },
  brick:     { gain: 0.98, thumpF: 98,  thumpDecay: 0.050, thumpPeak: 0.38, scuffF: 2600, scuffQ: 0.9, scuffDecay: 0.050, scuffPeak: 0.30, grains: 2, grainF: 2900, grainDecay: 0.040, grainPeak: 0.10, resF: 0,   resQ: 0,  resDecay: 0,     resPeak: 0,    gear: 0.16, lp: 11000 },
  plaster:   { gain: 0.86, thumpF: 90,  thumpDecay: 0.050, thumpPeak: 0.34, scuffF: 2000, scuffQ: 0.7, scuffDecay: 0.055, scuffPeak: 0.24, grains: 2, grainF: 2400, grainDecay: 0.045, grainPeak: 0.09, resF: 0,   resQ: 0,  resDecay: 0,     resPeak: 0,    gear: 0.14, lp: 9000 },
  tile:      { gain: 1.02, thumpF: 105, thumpDecay: 0.045, thumpPeak: 0.34, scuffF: 3800, scuffQ: 1.1, scuffDecay: 0.045, scuffPeak: 0.34, grains: 0, grainF: 4200, grainDecay: 0.035, grainPeak: 0.10, resF: 2100, resQ: 14, resDecay: 0.070, resPeak: 0.12, gear: 0.17, lp: 13000 },
  metal:     { gain: 1.06, thumpF: 120, thumpDecay: 0.050, thumpPeak: 0.36, scuffF: 3000, scuffQ: 1.0, scuffDecay: 0.045, scuffPeak: 0.30, grains: 0, grainF: 3600, grainDecay: 0.035, grainPeak: 0.10, resF: 520,  resQ: 13, resDecay: 0.190, resPeak: 0.24, gear: 0.18, lp: 14000 },
  metal_thin:{ gain: 1.04, thumpF: 135, thumpDecay: 0.045, thumpPeak: 0.30, scuffF: 3600, scuffQ: 1.1, scuffDecay: 0.040, scuffPeak: 0.30, grains: 0, grainF: 4200, grainDecay: 0.035, grainPeak: 0.10, resF: 1150, resQ: 18, resDecay: 0.170, resPeak: 0.24, gear: 0.18, lp: 15000 },
  wood:      { gain: 0.96, thumpF: 105, thumpDecay: 0.055, thumpPeak: 0.40, scuffF: 1700, scuffQ: 0.9, scuffDecay: 0.050, scuffPeak: 0.24, grains: 0, grainF: 2000, grainDecay: 0.040, grainPeak: 0.08, resF: 245,  resQ: 8,  resDecay: 0.100, resPeak: 0.22, gear: 0.15, lp: 9000 },
  glass:     { gain: 0.94, thumpF: 100, thumpDecay: 0.040, thumpPeak: 0.28, scuffF: 5200, scuffQ: 1.2, scuffDecay: 0.055, scuffPeak: 0.30, grains: 5, grainF: 6200, grainDecay: 0.060, grainPeak: 0.14, resF: 3400, resQ: 16, resDecay: 0.110, resPeak: 0.14, gear: 0.16, lp: 15000 },
  dirt:      { gain: 0.82, thumpF: 78,  thumpDecay: 0.060, thumpPeak: 0.38, scuffF: 1200, scuffQ: 0.6, scuffDecay: 0.070, scuffPeak: 0.22, grains: 3, grainF: 1500, grainDecay: 0.055, grainPeak: 0.10, resF: 0,   resQ: 0,  resDecay: 0,     resPeak: 0,    gear: 0.12, lp: 6500 },
  sand:      { gain: 0.74, thumpF: 70,  thumpDecay: 0.055, thumpPeak: 0.30, scuffF: 2600, scuffQ: 0.5, scuffDecay: 0.095, scuffPeak: 0.26, grains: 4, grainF: 4000, grainDecay: 0.080, grainPeak: 0.11, resF: 0,   resQ: 0,  resDecay: 0,     resPeak: 0,    gear: 0.11, lp: 8000 },
  gravel:    { gain: 0.92, thumpF: 84,  thumpDecay: 0.055, thumpPeak: 0.34, scuffF: 2200, scuffQ: 0.7, scuffDecay: 0.070, scuffPeak: 0.26, grains: 6, grainF: 3000, grainDecay: 0.065, grainPeak: 0.15, resF: 0,   resQ: 0,  resDecay: 0,     resPeak: 0,    gear: 0.14, lp: 10000 },
  grass:     { gain: 0.70, thumpF: 74,  thumpDecay: 0.055, thumpPeak: 0.28, scuffF: 3400, scuffQ: 0.5, scuffDecay: 0.090, scuffPeak: 0.24, grains: 4, grainF: 4800, grainDecay: 0.085, grainPeak: 0.11, resF: 0,   resQ: 0,  resDecay: 0,     resPeak: 0,    gear: 0.11, lp: 8500 },
  fabric:    { gain: 0.64, thumpF: 76,  thumpDecay: 0.050, thumpPeak: 0.26, scuffF: 1500, scuffQ: 0.6, scuffDecay: 0.065, scuffPeak: 0.18, grains: 1, grainF: 1800, grainDecay: 0.050, grainPeak: 0.06, resF: 0,   resQ: 0,  resDecay: 0,     resPeak: 0,    gear: 0.10, lp: 6000 },
  rubber:    { gain: 0.78, thumpF: 82,  thumpDecay: 0.050, thumpPeak: 0.34, scuffF: 1100, scuffQ: 0.8, scuffDecay: 0.055, scuffPeak: 0.20, grains: 0, grainF: 1400, grainDecay: 0.040, grainPeak: 0.06, resF: 165,  resQ: 5,  resDecay: 0.070, resPeak: 0.14, gear: 0.12, lp: 6000 },
  sandbag:   { gain: 0.72, thumpF: 72,  thumpDecay: 0.055, thumpPeak: 0.30, scuffF: 2000, scuffQ: 0.5, scuffDecay: 0.080, scuffPeak: 0.22, grains: 4, grainF: 3400, grainDecay: 0.075, grainPeak: 0.11, resF: 0,   resQ: 0,  resDecay: 0,     resPeak: 0,    gear: 0.11, lp: 7000 },
  water:     { gain: 0.90, thumpF: 90,  thumpDecay: 0.060, thumpPeak: 0.32, scuffF: 2800, scuffQ: 0.5, scuffDecay: 0.120, scuffPeak: 0.34, grains: 5, grainF: 5000, grainDecay: 0.110, grainPeak: 0.16, resF: 0,   resQ: 0,  resDecay: 0,     resPeak: 0,    gear: 0.12, lp: 11000 },
  flesh:     { gain: 0.80, thumpF: 68,  thumpDecay: 0.055, thumpPeak: 0.34, scuffF: 900,  scuffQ: 0.8, scuffDecay: 0.050, scuffPeak: 0.18, grains: 0, grainF: 900,  grainDecay: 0.040, grainPeak: 0.06, resF: 0,   resQ: 0,  resDecay: 0,     resPeak: 0,    gear: 0.10, lp: 5000 }
};

const EMPTY_OPTS = Object.freeze({});

export class AudioSystem {
  constructor(ctx) {
    this.ctx = ctx;

    // continua rodando com o jogo pausado: o listener e o filtro de abafamento
    // precisam acompanhar a camera mesmo no menu e na pausa.
    this.updateWhilePaused = true;

    this.enabled = false;
    this._ac = null;
    this._warned = false;

    /** @type {Array<object>} pool de vozes (cadeia de nos reaproveitada) */
    this._voices = [];
    /** @type {Array<object>} agenda de sons futuros (recarga, cartucho, ambiente) */
    this._sched = [];

    this._listenerPos = new THREE.Vector3();
    this._weaponFamily = new Map();

    // parametros do som que esta sendo tocado (evita alocar um objeto por som)
    this._pX = 0; this._pY = 0; this._pZ = 0;
    this._pPositional = false;
    this._pDist = 0;
    this._pWhen = 0;
    this._pVol = 1;
    this._pPitch = 1;
    this._pOccluded = false;
    this._pRef = 4.5;
    this._pRoll = 1.25;

    // objeto de opcoes reaproveitado pelos ouvintes de evento
    this._o = { position: null, volume: 1, pitch: 1, far: false, running: false, crouch: false, duration: 0, empty: false };

    this._losBudget = LOS_PER_FRAME;
    this._muffle = 22000;          // corte atual do filtro global (suavizado em JS)
    this._hitMuffle = 22000;       // abafamento temporario por dano levado
    this._ambientTarget = 0;
    this._ambientNow = 0;
    this._nextAmbientAt = 0;
    this._state = 'menu';
    this._started = false;
    this._offSettings = null;
    this._gestureOff = null;
    this._humLevel = 0;
  }

  // =========================================================================
  // ciclo de vida
  // =========================================================================

  async init() {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) { this._warn('Web Audio indisponivel neste navegador'); return; }

      this._ac = new AC({ latencyHint: 'interactive' });
      this._buildBuffers();
      this._buildGraph();
      this._buildVoices();
      this._buildScheduler();
      this._applyVolumes();
      this._bindEvents();
      this._bindGesture();

      this._offSettings = this.ctx.settings.onChange(() => this._applyVolumes());
      this.enabled = true;
    } catch (err) {
      this._warn(err);
      this.enabled = false;
    }
  }

  /** AudioContext so sai de 'suspended' depois de um gesto do usuario. */
  resume() {
    if (!this._ac) return;
    try {
      if (this._ac.state !== 'running') {
        const p = this._ac.resume();
        if (p && p.then) p.then(() => this._onRunning()).catch(() => {});
        else this._onRunning();
      } else {
        this._onRunning();
      }
    } catch (err) {
      this._warn(err);
    }
  }

  _onRunning() {
    if (this._started || !this._ac || this._ac.state !== 'running') return;
    this._started = true;
    this._startAmbience();
    this._nextAmbientAt = this._ac.currentTime + 6 + Math.random() * 8;
  }

  dispose() {
    try {
      this._offSettings?.();
      this._gestureOff?.();
      this.stopAll();
      this._ac?.close?.();
    } catch { /* encerrando: erro aqui nao importa */ }
    this.enabled = false;
    this._ac = null;
  }

  // =========================================================================
  // buffers, curvas e impulso de reverb - tudo gerado em codigo
  // =========================================================================

  _buildBuffers() {
    const ac = this._ac;
    const rate = ac.sampleRate;
    const n = Math.floor(rate * 2);

    const white = ac.createBuffer(1, n, rate);
    const wd = white.getChannelData(0);
    for (let i = 0; i < n; i++) wd[i] = Math.random() * 2 - 1;

    // ruido rosa (aproximacao de Paul Kellet): -3 dB/oitava, soa mais "natural"
    // que o branco em vento e em cauda de tiro.
    const pink = ac.createBuffer(1, n, rate);
    const pd = pink.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < n; i++) {
      const w = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.96900 * b2 + w * 0.1538520;
      b3 = 0.86650 * b3 + w * 0.3104856;
      b4 = 0.55000 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.0168980;
      pd[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
      b6 = w * 0.115926;
    }

    // ruido marrom: integrador com fuga. Serve de rumor grave da cidade.
    const brown = ac.createBuffer(1, n, rate);
    const bd = brown.getChannelData(0);
    let last = 0;
    for (let i = 0; i < n; i++) {
      const w = Math.random() * 2 - 1;
      last = (last + 0.02 * w) / 1.02;
      bd[i] = last * 3.2;
    }

    this._white = white;
    this._pink = pink;
    this._brown = brown;
    this._ir = this._makeImpulse(1.5, 3.1);
    this._curveSoft = this._makeCurve(2.2);
    this._curveHard = this._makeCurve(7.0);
  }

  /**
   * Impulso de rua: algumas reflexoes iniciais discretas (predios dos dois
   * lados) e cauda de ruido que escurece com o tempo. Curto de proposito -
   * reverb longo em FPS urbano soa como igreja.
   */
  _makeImpulse(seconds, decay) {
    const ac = this._ac;
    const rate = ac.sampleRate;
    const len = Math.max(16, Math.floor(seconds * rate));
    const buf = ac.createBuffer(2, len, rate);
    const early = [0.011, 0.019, 0.028, 0.041, 0.057, 0.073, 0.096];

    for (let c = 0; c < 2; c++) {
      const d = buf.getChannelData(c);
      let lp = 0;
      for (let i = 0; i < len; i++) {
        const t = i / len;
        const env = Math.pow(1 - t, decay);
        const w = Math.random() * 2 - 1;
        lp += (w - lp) * (0.36 - 0.26 * t);
        d[i] = lp * env * 0.9;
      }
      for (let k = 0; k < early.length; k++) {
        // deslocamento minimo entre canais = largura estereo sem soar duplicado
        const idx = Math.floor((early[k] + (c ? 0.0031 : 0)) * rate);
        if (idx > 0 && idx < len) d[idx] += (1 - k / early.length) * 0.5 * (Math.random() > 0.5 ? 1 : -1);
      }
    }
    return buf;
  }

  /** Curva tanh normalizada para o WaveShaper (saturacao macia ou dura). */
  _makeCurve(k) {
    const n = 1024;
    const c = new Float32Array(n);
    const norm = Math.tanh(k);
    for (let i = 0; i < n; i++) {
      const x = (i / (n - 1)) * 2 - 1;
      c[i] = Math.tanh(k * x) / norm;
    }
    return c;
  }

  // =========================================================================
  // grafo de audio
  // =========================================================================

  _buildGraph() {
    const ac = this._ac;

    this.master = ac.createGain();
    this.master.gain.value = 0.8;

    // limitador de seguranca: com 24 vozes e tiro automatico, sem isto estoura
    this.limiter = ac.createDynamicsCompressor();
    this.limiter.threshold.value = -10;
    this.limiter.knee.value = 14;
    this.limiter.ratio.value = 8;
    this.limiter.attack.value = 0.003;
    this.limiter.release.value = 0.19;

    // filtro global: abafa tudo quando o jogador esta ferido, morto ou pausado
    this.masterFilter = ac.createBiquadFilter();
    this.masterFilter.type = 'lowpass';
    this.masterFilter.frequency.value = 22000;
    this.masterFilter.Q.value = 0.5;

    this.masterFilter.connect(this.limiter);
    this.limiter.connect(this.master);
    this.master.connect(ac.destination);

    this.duck = ac.createGain();
    this.duck.gain.value = 1;
    this.duck.connect(this.masterFilter);

    this.sfxBus = ac.createGain();
    this.sfxBus.connect(this.duck);

    this.ambientBus = ac.createGain();
    this.ambientBus.gain.value = 0;
    this.ambientBus.connect(this.duck);

    // tiro proprio: fora do duck (nao pode abaixar a si mesmo) e com soft clip
    // proprio, que da a "cola" de arma potente sem passar do limite.
    this.shotShaper = ac.createWaveShaper();
    this.shotShaper.curve = this._curveSoft;
    this.shotBus = ac.createGain();
    this.shotBus.connect(this.shotShaper);
    this.shotShaper.connect(this.masterFilter);

    this.reverb = ac.createConvolver();
    this.reverb.buffer = this._ir;
    this.reverbBus = ac.createGain();
    this.reverbBus.gain.value = 1;
    this.reverbReturn = ac.createGain();
    this.reverbReturn.gain.value = 0.85;
    this.reverbBus.connect(this.reverb);
    this.reverb.connect(this.reverbReturn);
    this.reverbReturn.connect(this.masterFilter);
  }

  _buildVoices() {
    const ac = this._ac;
    // HRTF e caro; em qualidade baixa o panner simples ja resolve a direcao.
    const model = this.ctx.settings.quality === 'low' ? 'equalpower' : 'HRTF';

    for (let i = 0; i < MAX_VOICES; i++) {
      const input = ac.createGain();
      const lp = ac.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 22000;
      lp.Q.value = 0.4;
      const gain = ac.createGain();
      const panner = ac.createPanner();
      panner.panningModel = model;
      panner.distanceModel = 'inverse';
      panner.refDistance = 4.5;
      panner.maxDistance = 400;
      panner.rolloffFactor = 1.25;
      const send = ac.createGain();
      send.gain.value = 0;

      input.connect(lp);
      lp.connect(gain);
      gain.connect(send);
      send.connect(this.reverbBus);

      this._voices.push({
        input, lp, gain, panner, send,
        nodes: [], active: false, chain: 0, bus: null,
        startedAt: 0, endsAt: 0, priority: 0
      });
    }
  }

  _buildScheduler() {
    for (let i = 0; i < MAX_SCHEDULED; i++) {
      this._sched.push({
        active: false, at: 0, name: '',
        volume: 1, pitch: 1, positional: false, x: 0, y: 0, z: 0
      });
    }
  }

  _applyVolumes() {
    if (!this._ac) return;
    const s = this.ctx.settings;
    const clamp = (v, d) => (typeof v === 'number' && isFinite(v) ? Math.max(0, Math.min(1, v)) : d);
    const mv = clamp(s.masterVolume, 0.8);
    const sv = clamp(s.sfxVolume, 1.0);
    const bv = clamp(s.musicVolume, 0.5);
    this.master.gain.value = mv;
    this.sfxBus.gain.value = sv;
    this.shotBus.gain.value = sv * 0.9;
    this._ambientGainScale = bv;
  }

  // =========================================================================
  // listener e laco por frame
  // =========================================================================

  /** Posiciona o ouvinte a partir da camera do jogo (posicao + orientacao). */
  setListener(camera, dt) {
    const ac = this._ac;
    if (!ac || !camera) return;
    const l = ac.listener;

    camera.getWorldPosition(_tmpPos);
    camera.getWorldDirection(_tmpFwd);
    const e = camera.matrixWorld.elements;
    _tmpUp.set(e[4], e[5], e[6]);

    // rampa curta ate o proximo frame: evita "zíper" quando a camera gira rapido
    const t = ac.currentTime + Math.min(0.05, Math.max(0.008, dt || 0.016));

    if (l.positionX) {
      l.positionX.linearRampToValueAtTime(_tmpPos.x, t);
      l.positionY.linearRampToValueAtTime(_tmpPos.y, t);
      l.positionZ.linearRampToValueAtTime(_tmpPos.z, t);
      l.forwardX.linearRampToValueAtTime(_tmpFwd.x, t);
      l.forwardY.linearRampToValueAtTime(_tmpFwd.y, t);
      l.forwardZ.linearRampToValueAtTime(_tmpFwd.z, t);
      l.upX.linearRampToValueAtTime(_tmpUp.x, t);
      l.upY.linearRampToValueAtTime(_tmpUp.y, t);
      l.upZ.linearRampToValueAtTime(_tmpUp.z, t);
    } else if (l.setPosition) {
      l.setPosition(_tmpPos.x, _tmpPos.y, _tmpPos.z);
      l.setOrientation(_tmpFwd.x, _tmpFwd.y, _tmpFwd.z, _tmpUp.x, _tmpUp.y, _tmpUp.z);
    }

    this._listenerPos.copy(_tmpPos);
  }

  update(dt) {
    if (!this.enabled || !this._ac) return;
    try {
      const now = this._ac.currentTime;
      this._losBudget = LOS_PER_FRAME;

      this.setListener(this.ctx.camera, dt);

      for (let i = 0; i < this._voices.length; i++) {
        const v = this._voices[i];
        if (v.active && now >= v.endsAt) this._release(v);
      }

      this._runScheduler(now);
      this._updateHealthAudio(dt);
      this._updateAmbience(dt, now);
    } catch (err) {
      this._warn(err);
    }
  }

  // =========================================================================
  // pool de vozes
  // =========================================================================

  _acquire(priority, when, duration, positional, bus) {
    let v = null;
    for (let i = 0; i < this._voices.length; i++) {
      if (!this._voices[i].active) { v = this._voices[i]; break; }
    }
    if (!v) {
      // rouba a de menor prioridade e, no empate, a mais antiga
      let best = null;
      for (let i = 0; i < this._voices.length; i++) {
        const c = this._voices[i];
        if (!best || c.priority < best.priority ||
            (c.priority === best.priority && c.startedAt < best.startedAt)) best = c;
      }
      if (!best || best.priority > priority) return null;
      this._release(best);
      v = best;
    }

    v.active = true;
    v.priority = priority;
    v.startedAt = when;
    v.endsAt = when + duration;
    v.bus = bus;

    v.gain.gain.cancelScheduledValues(0);
    v.gain.gain.value = 1;
    v.input.gain.cancelScheduledValues(0);
    v.input.gain.value = 1;
    v.lp.frequency.cancelScheduledValues(0);
    v.lp.frequency.value = 22000;
    v.lp.Q.value = 0.4;
    v.send.gain.value = 0;

    if (positional) {
      v.gain.connect(v.panner);
      v.panner.connect(bus);
      v.chain = 1;
    } else {
      v.gain.connect(bus);
      v.chain = 2;
    }
    return v;
  }

  _release(v) {
    for (let i = 0; i < v.nodes.length; i++) {
      const n = v.nodes[i];
      try { if (n.stop) n.stop(); } catch { /* nunca iniciou ou ja parou */ }
      try { n.disconnect(); } catch { /* ja desconectado */ }
    }
    v.nodes.length = 0;
    try {
      if (v.chain === 1) { v.gain.disconnect(v.panner); v.panner.disconnect(v.bus); }
      else if (v.chain === 2) { v.gain.disconnect(v.bus); }
    } catch { /* ja desconectado */ }
    v.chain = 0;
    v.bus = null;
    v.active = false;
    v.priority = 0;
    v.endsAt = 0;
  }

  stopAll() {
    if (!this._ac) return;
    try {
      for (let i = 0; i < this._voices.length; i++) {
        if (this._voices[i].active) this._release(this._voices[i]);
      }
      for (let i = 0; i < this._sched.length; i++) this._sched[i].active = false;
      this._setAmbience(0);
    } catch (err) {
      this._warn(err);
    }
  }

  /** Prepara a voz ja com panner, absorcao do ar e oclusao aplicados. */
  _voiceFor(priority, duration, bus) {
    const v = this._acquire(priority, this._pWhen, duration + 0.06, this._pPositional, bus);
    if (!v) return null;

    if (this._pPositional) {
      v.panner.refDistance = this._pRef;
      v.panner.rolloffFactor = this._pRoll;
      this._setPannerPos(v.panner, this._pX, this._pY, this._pZ);
      // absorcao do ar: quanto mais longe, menos agudo chega
      let f = Math.max(700, 20000 * Math.pow(0.964, this._pDist));
      let g = this._pVol;
      if (this._pOccluded) { f = Math.min(f, 520); g *= 0.5; }
      v.lp.frequency.value = f;
      v.gain.gain.value = g;
    } else {
      v.lp.frequency.value = 22000;
      v.gain.gain.value = this._pVol;
    }
    return v;
  }

  _setPannerPos(panner, x, y, z) {
    if (panner.positionX) {
      panner.positionX.value = x;
      panner.positionY.value = y;
      panner.positionZ.value = z;
    } else if (panner.setPosition) {
      panner.setPosition(x, y, z);
    }
  }

  // =========================================================================
  // camadas elementares
  // =========================================================================

  /** Camada de ruido filtrado com envelope AD. Le os campos de L. */
  _spawnNoise(v, when) {
    const ac = this._ac;
    const buf = L.buf || this._white;
    const src = ac.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.playbackRate.value = Math.max(0.05, L.rate);

    let node = src;
    let filt = null;
    if (L.type !== 'none') {
      filt = ac.createBiquadFilter();
      filt.type = L.type;
      filt.frequency.value = Math.max(20, Math.min(20000, L.f0));
      filt.Q.value = Math.max(0.0001, L.q);
      node.connect(filt);
      node = filt;
    }
    let shaper = null;
    if (L.drive > 0) {
      shaper = ac.createWaveShaper();
      shaper.curve = L.drive > 4 ? this._curveHard : this._curveSoft;
      node.connect(shaper);
      node = shaper;
    }

    const g = ac.createGain();
    node.connect(g);
    g.connect(v.input);

    const t0 = when + L.delay;
    const atk = Math.max(0.0004, L.attack);
    const dec = Math.max(0.005, L.decay);
    const peak = Math.max(0.0002, L.peak);

    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(peak, t0 + atk);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + atk + dec);

    if (filt && L.f1 > 0) {
      filt.frequency.setValueAtTime(Math.max(20, Math.min(20000, L.f0)), t0);
      filt.frequency.exponentialRampToValueAtTime(Math.max(20, Math.min(20000, L.f1)), t0 + atk + dec);
    }

    // deslocamento aleatorio no buffer = grao diferente a cada disparo, que e o
    // que evita o efeito "metralhadora de brinquedo" de repetir a mesma amostra
    const off = Math.random() * Math.max(0.01, buf.duration - 0.6);
    src.start(t0, off);
    src.stop(t0 + atk + dec + 0.03);

    v.nodes.push(src);
    if (filt) v.nodes.push(filt);
    if (shaper) v.nodes.push(shaper);
    v.nodes.push(g);
    return L.delay + atk + dec;
  }

  /** Camada tonal (oscilador) com varredura de frequencia e envelope AD. */
  _spawnTone(v, when) {
    const ac = this._ac;
    const osc = ac.createOscillator();
    osc.type = L.wave;

    const t0 = when + L.delay;
    const atk = Math.max(0.0004, L.attack);
    const dec = Math.max(0.005, L.decay);
    const f0 = Math.max(10, L.f0);
    const f1 = L.f1 > 0 ? Math.max(10, L.f1) : f0;

    osc.frequency.setValueAtTime(f0, t0);
    if (f1 !== f0) osc.frequency.exponentialRampToValueAtTime(f1, t0 + (atk + dec) * 0.9);

    let node = osc;
    let filt = null;
    if (L.cut > 0 && L.type !== 'none') {
      filt = ac.createBiquadFilter();
      filt.type = L.type;
      filt.frequency.value = Math.max(20, Math.min(20000, L.cut));
      filt.Q.value = Math.max(0.0001, L.q);
      node.connect(filt);
      node = filt;
    }

    const g = ac.createGain();
    node.connect(g);
    g.connect(v.input);

    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(Math.max(0.0002, L.peak), t0 + atk);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + atk + dec);

    osc.start(t0);
    osc.stop(t0 + atk + dec + 0.03);

    v.nodes.push(osc);
    if (filt) v.nodes.push(filt);
    v.nodes.push(g);
    return L.delay + atk + dec;
  }

  // =========================================================================
  // API publica
  // =========================================================================

  /**
   * Toca um som sintetizado.
   * @param {string} name  ex.: 'shot.rifle', 'impact.metal', 'footstep.gravel'
   * @param {{position?:object, volume?:number, pitch?:number}} [opts]
   */
  play(name, opts) {
    if (!this.enabled || !this._ac) return null;
    if (this._ac.state !== 'running') return null;
    try {
      return this._playInternal(name, opts || EMPTY_OPTS);
    } catch (err) {
      this._warn(err);
      return null;
    }
  }

  _playInternal(name, o) {
    if (!name) return null;
    const ac = this._ac;

    // posicao
    let positional = false;
    let x = 0, y = 0, z = 0;
    const p = o.position;
    if (p) {
      if (typeof p.x === 'number') { x = p.x; y = p.y; z = p.z; positional = true; }
      else if (typeof p[0] === 'number') { x = p[0]; y = p[1]; z = p[2]; positional = true; }
    }

    let dist = 0;
    if (positional) {
      const dx = x - this._listenerPos.x;
      const dy = y - this._listenerPos.y;
      const dz = z - this._listenerPos.z;
      dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    }

    // atraso de propagacao: som distante chega depois. Sutil, mas e o que faz o
    // tiro do outro lado do mapa "acontecer la" e nao na cara do jogador.
    let travel = 0;
    if (dist > 6) travel = Math.min(MAX_TRAVEL_DELAY, (dist - 6) / SOUND_SPEED);

    // oclusao: parede entre fonte e ouvinte -> passa-baixa forte
    let occluded = false;
    if (positional && dist > 3 && this._losBudget > 0) {
      this._losBudget--;
      try {
        _tmpA.copy(this._listenerPos);
        _tmpB.set(x, y, z);
        const los = this.ctx.collision?.lineOfSight?.(_tmpA, _tmpB);
        if (los === false) occluded = true;
      } catch { /* colisao ainda incompleta: seguir sem oclusao */ }
    }

    this._pX = x; this._pY = y; this._pZ = z;
    this._pPositional = positional;
    this._pDist = dist;
    this._pWhen = ac.currentTime + 0.006 + travel;
    this._pVol = typeof o.volume === 'number' && isFinite(o.volume) ? Math.max(0, Math.min(4, o.volume)) : 1;
    this._pPitch = typeof o.pitch === 'number' && isFinite(o.pitch) && o.pitch > 0 ? o.pitch : 1;
    this._pOccluded = occluded;
    this._pRef = 4.5;
    this._pRoll = 1.25;

    const dot = name.indexOf('.');
    const base = dot < 0 ? name : name.slice(0, dot);
    const variant = dot < 0 ? '' : name.slice(dot + 1);

    switch (base) {
      case 'shot':      return this._playShot(variant || 'rifle', !!o.far);
      case 'impact':    return this._playImpact(variant);
      case 'footstep':  return this._playFootstep(variant, !!o.running, !!o.crouch);
      case 'land':      return this._playLand(variant, this._pVol);
      case 'jump':      return this._playJump();
      case 'reload':    return this._playReload(variant, o);
      case 'weapon':    return this._playWeaponAction(variant);
      case 'hitmarker': return this._playHitmarker(variant);
      case 'player':    return this._playPlayerVoice(variant);
      case 'body':      return this._playBodyFall();
      case 'wave':      return this._playWaveSting(variant);
      case 'game':      return this._playGameSting(variant);
      case 'ui':        return this._playUi(variant);
      case 'shell':     return this._playShell();
      case 'whizz':     return this._playWhizz();
      case 'gear':      return this._playGear(this._pVol);
      case 'explosion': return this._playExplosion();
      case 'ambient':   return this._playAmbientEvent(variant);
      default:
        this._warn(`som desconhecido: ${name}`);
        return null;
    }
  }

  // =========================================================================
  // tiro
  // =========================================================================

  _playShot(family, far) {
    const prof = SHOT_PROFILES[family] || SHOT_PROFILES.rifle;
    const pitch = this._pPitch * (1 + (Math.random() - 0.5) * prof.jitter);
    const dist = this._pDist;
    // quanto mais longe, menos transiente e mais cauda - e assim que o ouvido
    // mede distancia de tiro no mundo real
    const far01 = far ? Math.min(1, Math.max(0, (dist - 8) / 55)) : 0;

    const tail = prof.tailDecay * (1 + far01 * 1.7);
    const dur = 0.14 + tail;

    // tiro perde pouco volume com a distancia (rolloff baixo, ref alta)
    this._pRef = far ? 14 : 6;
    this._pRoll = far ? 0.72 : 1.0;
    this._pVol *= prof.gain * (far ? 1.35 : 1);

    const bus = far ? this.sfxBus : this.shotBus;
    const v = this._voiceFor(far ? 6 : 10, dur, bus);
    if (!v) return null;
    v.send.gain.value = prof.send * (1 + far01 * 0.9);

    const w = this._pWhen;

    // 1. transiente: estalo curtissimo que da o "ataque"
    const clickPeak = prof.clickPeak * (1 - far01 * 0.85);
    if (clickPeak > 0.02) {
      Lreset();
      L.buf = this._white; L.type = 'highpass';
      L.f0 = prof.clickF * pitch; L.q = 0.7;
      L.peak = clickPeak; L.attack = 0.0006; L.decay = prof.clickDecay;
      L.drive = 6;
      this._spawnNoise(v, w);
    }

    // 2. corpo: ruido com corte descendo (a "explosao" da carga)
    Lreset();
    L.buf = this._white; L.type = 'lowpass';
    L.f0 = prof.bodyF0 * pitch * (1 - far01 * 0.45);
    L.f1 = prof.bodyF1 * pitch;
    L.q = prof.bodyQ;
    L.peak = prof.bodyPeak * (1 - far01 * 0.30);
    L.attack = 0.0012; L.decay = prof.bodyDecay * (1 + far01 * 0.5);
    L.drive = prof.drive;
    this._spawnNoise(v, w);

    // 3. sub: o "murro" grave. Longe, o grave e o que mais sobrevive.
    Lreset();
    L.wave = 'triangle';
    L.f0 = prof.subF0 * pitch; L.f1 = prof.subF1 * pitch;
    L.type = 'lowpass'; L.cut = 520; L.q = 0.7;
    L.peak = prof.subPeak * (1 + far01 * 0.55);
    L.attack = 0.001; L.decay = prof.subDecay * (1 + far01 * 0.6);
    this._spawnTone(v, w);

    // 4. crack agudo (supersonico)
    const crackPeak = prof.crackPeak * (1 - far01 * 0.9);
    if (crackPeak > 0.02) {
      Lreset();
      L.buf = this._white; L.type = 'bandpass';
      L.f0 = prof.crackF * pitch; L.f1 = prof.crackF * 0.55 * pitch;
      L.q = prof.crackQ;
      L.peak = crackPeak; L.attack = 0.0008; L.decay = prof.crackDecay;
      this._spawnNoise(v, w);
    }

    // 5. mecanica do ferrolho - so no tiro proprio (ninguem ouve isso de longe)
    if (!far) {
      Lreset();
      L.buf = this._white; L.type = 'bandpass';
      L.f0 = prof.mechF * pitch; L.q = 7;
      L.peak = prof.mechPeak; L.attack = 0.0008; L.decay = 0.030;
      L.delay = prof.mechDelay;
      this._spawnNoise(v, w);
    }

    // 6. cauda: o eco da rua
    Lreset();
    L.buf = this._pink; L.rate = 0.9; L.type = 'bandpass';
    L.f0 = prof.tailF0 * (1 - far01 * 0.4);
    L.f1 = prof.tailF1 * (1 - far01 * 0.35);
    L.q = 0.75;
    L.peak = prof.tailPeak * (1 + far01 * 1.6);
    L.attack = 0.012 + far01 * 0.03;
    L.decay = tail;
    this._spawnNoise(v, w);

    if (!far) {
      this._duck(w, 0.55, 0.08);
      if (Math.random() < 0.7) {
        this._schedule('shell', 0.30 + Math.random() * 0.22, 0.22, 0.9 + Math.random() * 0.35, false, 0, 0, 0);
      }
    }
    return v;
  }

  /** Abaixa tudo (menos o proprio tiro) para o disparo "socar". */
  _duck(when, amount, hold) {
    const g = this.duck.gain;
    const t = Math.max(when, this._ac.currentTime);
    try {
      if (g.cancelAndHoldAtTime) g.cancelAndHoldAtTime(t);
      else { const cur = g.value; g.cancelScheduledValues(t); g.setValueAtTime(cur, t); }
      g.linearRampToValueAtTime(amount, t + 0.010);
      g.setValueAtTime(amount, t + hold);
      g.linearRampToValueAtTime(1, t + hold + 0.13);
    } catch { /* param ocupado: proximo tiro corrige */ }
  }

  /** Cartucho caindo no chao: dois tinidos metalicos curtos com quique. */
  _playShell() {
    const v = this._voiceFor(1, 0.30, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    v.send.gain.value = 0.22;
    const base = 3400 + Math.random() * 1400;
    for (let i = 0; i < 3; i++) {
      Lreset();
      L.buf = this._white; L.type = 'bandpass';
      L.f0 = base * (1 + i * 0.12); L.q = 16;
      L.peak = 0.20 * Math.pow(0.55, i);
      L.attack = 0.0006; L.decay = 0.055 - i * 0.012;
      L.delay = i === 0 ? 0 : 0.075 * i + Math.random() * 0.03;
      this._spawnNoise(v, w);
    }
    return v;
  }

  /** Assobio da bala passando perto do ouvido. */
  _playWhizz() {
    this._pRef = 3;
    this._pRoll = 1.6;
    const v = this._voiceFor(7, 0.14, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 4200 + Math.random() * 1200; L.f1 = 1300; L.q = 2.4;
    L.peak = 0.40; L.attack = 0.004; L.decay = 0.085;
    this._spawnNoise(v, w);
    Lreset();
    L.buf = this._white; L.type = 'highpass';
    L.f0 = 6000; L.q = 0.7;
    L.peak = 0.18; L.attack = 0.001; L.decay = 0.030;
    this._spawnNoise(v, w);
    return v;
  }

  // =========================================================================
  // impacto por superficie
  // =========================================================================

  _playImpact(surface) {
    const prof = IMPACT_PROFILES[this._surfaceOf(surface)] || IMPACT_PROFILES.concrete;
    const pitch = this._pPitch * (0.92 + Math.random() * 0.16);
    const dur = 0.12 + Math.max(prof.bodyDecay, prof.resDecay) + prof.grainDecay;

    this._pRef = 5;
    this._pRoll = 1.5;
    this._pVol *= prof.gain;

    const v = this._voiceFor(4, dur, this.sfxBus);
    if (!v) return null;
    v.send.gain.value = prof.send;
    const w = this._pWhen;

    // estalo do projetil entrando
    Lreset();
    L.buf = this._white; L.type = 'highpass';
    L.f0 = prof.clickF * pitch; L.q = 0.8;
    L.peak = prof.clickPeak; L.attack = 0.0005; L.decay = prof.clickDecay;
    L.drive = prof.drive;
    this._spawnNoise(v, w);

    // corpo do impacto
    Lreset();
    L.buf = this._white; L.type = 'lowpass';
    L.f0 = prof.bodyF * pitch; L.f1 = prof.bodyF * 0.35 * pitch; L.q = prof.bodyQ;
    L.peak = prof.bodyPeak; L.attack = 0.0010; L.decay = prof.bodyDecay;
    L.drive = prof.drive > 2 ? prof.drive : 0;
    this._spawnNoise(v, w);

    // ressonancia (metal canta, madeira faz "toc", vidro tilinta)
    if (prof.resPeak > 0) {
      Lreset();
      L.buf = this._white; L.type = 'bandpass';
      L.f0 = prof.resF * pitch; L.q = prof.resQ;
      L.peak = prof.resPeak; L.attack = 0.0008; L.decay = prof.resDecay;
      this._spawnNoise(v, w);
      if (prof.resTone) {
        Lreset();
        L.wave = 'sine';
        L.f0 = prof.resF * pitch * (1 + (Math.random() - 0.5) * 0.06);
        L.f1 = prof.resF * pitch * 0.94;
        L.type = 'none'; L.cut = 0;
        L.peak = prof.resPeak * 0.55; L.attack = 0.0012; L.decay = prof.resDecay * 1.15;
        this._spawnTone(v, w);
      }
    }

    // cacos/lascas caindo depois
    for (let i = 0; i < prof.grains; i++) {
      Lreset();
      L.buf = this._white; L.type = 'bandpass';
      L.f0 = prof.grainF * (0.7 + Math.random() * 0.8); L.q = 6 + Math.random() * 8;
      L.peak = prof.grainPeak * (0.5 + Math.random() * 0.6);
      L.attack = 0.0006; L.decay = prof.grainDecay * (0.6 + Math.random() * 0.8);
      L.delay = 0.012 + Math.random() * 0.11;
      this._spawnNoise(v, w);
    }
    return v;
  }

  // =========================================================================
  // passos, pulo e aterrissagem
  // =========================================================================

  _playFootstep(surface, running, crouch) {
    const prof = STEP_PROFILES[this._surfaceOf(surface)] || STEP_PROFILES.concrete;
    const pitch = this._pPitch * (0.94 + Math.random() * 0.12) * (running ? 1.05 : 1) * (crouch ? 0.94 : 1);
    const inten = crouch ? 0.42 : (running ? 1.28 : 1.0);
    const dur = 0.10 + Math.max(prof.scuffDecay, prof.resDecay, prof.grainDecay) * 1.6;

    this._pRef = 3.5;
    this._pRoll = 2.1;   // passo some rapido com a distancia
    // inten ja escala cada camada; aplicar de novo aqui dobraria o efeito
    this._pVol *= prof.gain * 0.9;

    const v = this._voiceFor(2, dur, this.sfxBus);
    if (!v) return null;
    v.send.gain.value = 0.14;
    v.lp.frequency.value = Math.min(v.lp.frequency.value, prof.lp * (crouch ? 0.6 : 1));
    const w = this._pWhen;

    // peso do corpo
    Lreset();
    L.wave = 'sine';
    L.f0 = prof.thumpF * pitch; L.f1 = prof.thumpF * 0.62 * pitch;
    L.type = 'lowpass'; L.cut = 300; L.q = 0.7;
    L.peak = prof.thumpPeak * inten;
    L.attack = 0.0015; L.decay = prof.thumpDecay;
    this._spawnTone(v, w);

    // atrito da bota
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = prof.scuffF * pitch; L.f1 = prof.scuffF * 0.55 * pitch; L.q = prof.scuffQ;
    L.peak = prof.scuffPeak * inten;
    L.attack = 0.0018; L.decay = prof.scuffDecay * (running ? 0.85 : 1);
    this._spawnNoise(v, w);

    // ressonancia do piso (chapa metalica, tabua)
    if (prof.resPeak > 0) {
      Lreset();
      L.buf = this._white; L.type = 'bandpass';
      L.f0 = prof.resF * pitch; L.q = prof.resQ;
      L.peak = prof.resPeak * inten; L.attack = 0.001; L.decay = prof.resDecay;
      this._spawnNoise(v, w);
    }

    // pedrisco solto
    const grains = prof.grains + (running ? 2 : 0);
    for (let i = 0; i < grains; i++) {
      Lreset();
      L.buf = this._white; L.type = 'bandpass';
      L.f0 = prof.grainF * (0.7 + Math.random() * 0.7); L.q = 5 + Math.random() * 7;
      L.peak = prof.grainPeak * inten * (0.4 + Math.random() * 0.7);
      L.attack = 0.0008; L.decay = prof.grainDecay * (0.6 + Math.random());
      L.delay = 0.008 + Math.random() * 0.07;
      this._spawnNoise(v, w);
    }

    // equipamento balancando - o detalhe que faz soar "soldado equipado"
    if (prof.gear > 0 && !crouch) {
      Lreset();
      L.buf = this._white; L.type = 'bandpass';
      L.f0 = 5200 + Math.random() * 2200; L.q = 3.5;
      L.peak = prof.gear * inten * 0.6;
      L.attack = 0.003; L.decay = 0.055;
      L.delay = 0.015 + Math.random() * 0.03;
      this._spawnNoise(v, w);
    }
    return v;
  }

  _playJump() {
    const v = this._voiceFor(3, 0.26, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    v.send.gain.value = 0.10;
    // roupa e equipamento esticando
    Lreset();
    L.buf = this._pink; L.rate = 1.3; L.type = 'bandpass';
    L.f0 = 1800; L.f1 = 3600; L.q = 0.6;
    L.peak = 0.22; L.attack = 0.010; L.decay = 0.13;
    this._spawnNoise(v, w);
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 5600; L.q = 3;
    L.peak = 0.12; L.attack = 0.004; L.decay = 0.09; L.delay = 0.02;
    this._spawnNoise(v, w);
    return v;
  }

  _playLand(surface, force) {
    const prof = STEP_PROFILES[this._surfaceOf(surface)] || STEP_PROFILES.concrete;
    const f = Math.max(0.35, Math.min(2.2, force || 1));
    const dur = 0.35 + prof.resDecay;

    this._pRef = 4;
    this._pRoll = 1.8;
    // a forca do impacto ja entra nos picos das camadas (f); aqui fica so o
    // ganho da superficie, senao uma queda alta multiplicaria duas vezes
    this._pVol = prof.gain;

    const v = this._voiceFor(5, dur, this.sfxBus);
    if (!v) return null;
    v.send.gain.value = 0.20;
    const w = this._pWhen;

    Lreset();
    L.wave = 'sine';
    L.f0 = prof.thumpF * 0.85; L.f1 = prof.thumpF * 0.45;
    L.type = 'lowpass'; L.cut = 260; L.q = 0.7;
    L.peak = Math.min(0.95, prof.thumpPeak * 1.9 * f);
    L.attack = 0.002; L.decay = prof.thumpDecay * 2.1;
    this._spawnTone(v, w);

    Lreset();
    L.buf = this._white; L.type = 'lowpass';
    L.f0 = prof.scuffF * 1.3; L.f1 = prof.scuffF * 0.4; L.q = 0.8;
    L.peak = Math.min(0.8, prof.scuffPeak * 1.7 * f);
    L.attack = 0.002; L.decay = prof.scuffDecay * 2.0;
    L.drive = 3;
    this._spawnNoise(v, w);

    // as duas botas nao batem no mesmo instante
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = prof.scuffF * 0.8; L.q = 1.2;
    L.peak = 0.22 * f; L.attack = 0.0015; L.decay = 0.07; L.delay = 0.045;
    this._spawnNoise(v, w);

    // equipamento chacoalhando na queda
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 4800; L.q = 2.5;
    L.peak = 0.16 * f; L.attack = 0.004; L.decay = 0.16; L.delay = 0.03;
    this._spawnNoise(v, w);

    if (prof.resPeak > 0) {
      Lreset();
      L.buf = this._white; L.type = 'bandpass';
      L.f0 = prof.resF; L.q = prof.resQ;
      L.peak = prof.resPeak * 1.5 * f; L.attack = 0.001; L.decay = prof.resDecay * 1.6;
      this._spawnNoise(v, w);
    }
    return v;
  }

  // =========================================================================
  // manuseio da arma
  // =========================================================================

  /**
   * Recarga em 3 tempos (4 com ferrolho quando a arma zerou):
   * soltar o pente, encaixar o pente novo, assentar, engatilhar.
   * Os tempos sao agendados como fracao da duracao real informada pela arma.
   */
  _playReload(variant, o) {
    if (variant === 'magout') return this._reloadMagOut();
    if (variant === 'magin') return this._reloadMagIn();
    if (variant === 'charge') return this._reloadCharge();
    if (variant === 'click') return this._reloadClick();

    const d = typeof o.duration === 'number' && o.duration > 0.2 ? Math.min(5, o.duration) : 2.2;
    const empty = !!o.empty;
    this._schedule('reload.click', 0.03, 1, 1, false, 0, 0, 0);
    this._schedule('reload.magout', d * 0.26, 1, 1, false, 0, 0, 0);
    this._schedule('reload.magin', d * (empty ? 0.58 : 0.64), 1, 1, false, 0, 0, 0);
    if (empty) this._schedule('reload.charge', d * 0.86, 1, 1, false, 0, 0, 0);
    return null;
  }

  /** Trava do carregador: click seco e alto de mola. */
  _reloadClick() {
    const v = this._voiceFor(8, 0.09, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    v.send.gain.value = 0.16;
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 3600; L.q = 9; L.peak = 0.34; L.attack = 0.0005; L.decay = 0.022;
    this._spawnNoise(v, w);
    Lreset();
    L.buf = this._white; L.type = 'highpass';
    L.f0 = 6500; L.q = 0.7; L.peak = 0.16; L.attack = 0.0004; L.decay = 0.012;
    this._spawnNoise(v, w);
    return v;
  }

  /** Pente saindo: raspar de metal + baque do pente batendo na mao. */
  _reloadMagOut() {
    const v = this._voiceFor(8, 0.30, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    v.send.gain.value = 0.22;
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 2600; L.f1 = 1500; L.q = 2.2;
    L.peak = 0.26; L.attack = 0.004; L.decay = 0.10;
    this._spawnNoise(v, w);
    Lreset();
    L.wave = 'triangle'; L.f0 = 200; L.f1 = 120;
    L.type = 'lowpass'; L.cut = 700; L.q = 0.8;
    L.peak = 0.30; L.attack = 0.001; L.decay = 0.07; L.delay = 0.075;
    this._spawnTone(v, w);
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 1900; L.q = 5; L.peak = 0.24; L.attack = 0.0006; L.decay = 0.05; L.delay = 0.075;
    this._spawnNoise(v, w);
    return v;
  }

  /** Pente novo entrando e travando: batida grave + clack agudo. */
  _reloadMagIn() {
    const v = this._voiceFor(8, 0.32, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    v.send.gain.value = 0.26;
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 1500; L.f1 = 900; L.q = 1.6;
    L.peak = 0.30; L.attack = 0.003; L.decay = 0.07;
    this._spawnNoise(v, w);
    Lreset();
    L.wave = 'triangle'; L.f0 = 165; L.f1 = 95;
    L.type = 'lowpass'; L.cut = 600; L.q = 0.8;
    L.peak = 0.42; L.attack = 0.0012; L.decay = 0.085; L.delay = 0.055;
    this._spawnTone(v, w);
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 3200; L.q = 10; L.peak = 0.34; L.attack = 0.0005; L.decay = 0.035; L.delay = 0.058;
    this._spawnNoise(v, w);
    return v;
  }

  /** Ferrolho: puxa (raspa) e solta (batida metalica alta). */
  _reloadCharge() {
    const v = this._voiceFor(8, 0.34, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    v.send.gain.value = 0.34;
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 2200; L.f1 = 3400; L.q = 1.8;
    L.peak = 0.24; L.attack = 0.006; L.decay = 0.075;
    this._spawnNoise(v, w);
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 2800; L.q = 12; L.peak = 0.44; L.attack = 0.0005; L.decay = 0.055; L.delay = 0.10;
    this._spawnNoise(v, w);
    Lreset();
    L.wave = 'sine'; L.f0 = 520; L.f1 = 380;
    L.type = 'bandpass'; L.cut = 520; L.q = 6;
    L.peak = 0.26; L.attack = 0.001; L.decay = 0.13; L.delay = 0.10;
    this._spawnTone(v, w);
    return v;
  }

  _playWeaponAction(variant) {
    if (variant === 'dry') {
      const v = this._voiceFor(8, 0.08, this.sfxBus);
      if (!v) return null;
      const w = this._pWhen;
      Lreset();
      L.buf = this._white; L.type = 'bandpass';
      L.f0 = 2400; L.q = 12; L.peak = 0.30; L.attack = 0.0004; L.decay = 0.020;
      this._spawnNoise(v, w);
      Lreset();
      L.buf = this._white; L.type = 'highpass';
      L.f0 = 7000; L.q = 0.7; L.peak = 0.10; L.attack = 0.0004; L.decay = 0.010;
      this._spawnNoise(v, w);
      return v;
    }
    if (variant === 'ads') return this._playGear(0.5);
    // troca de arma: pano + metal batendo no colete
    const v = this._voiceFor(8, 0.42, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    v.send.gain.value = 0.20;
    Lreset();
    L.buf = this._pink; L.rate = 1.2; L.type = 'bandpass';
    L.f0 = 1600; L.f1 = 3200; L.q = 0.6;
    L.peak = 0.24; L.attack = 0.012; L.decay = 0.19;
    this._spawnNoise(v, w);
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 2900; L.q = 8; L.peak = 0.26; L.attack = 0.0006; L.decay = 0.05; L.delay = 0.13;
    this._spawnNoise(v, w);
    Lreset();
    L.wave = 'triangle'; L.f0 = 210; L.f1 = 130;
    L.type = 'lowpass'; L.cut = 700; L.q = 0.7;
    L.peak = 0.24; L.attack = 0.0015; L.decay = 0.07; L.delay = 0.135;
    this._spawnTone(v, w);
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 5000; L.q = 3; L.peak = 0.12; L.attack = 0.004; L.decay = 0.10; L.delay = 0.21;
    this._spawnNoise(v, w);
    return v;
  }

  /** Farfalhar de equipamento (mira, alerta de inimigo, movimento perto). */
  _playGear(scale) {
    const s = Math.max(0.1, Math.min(2, scale || 1));
    this._pRef = 4;
    this._pRoll = 2.4;
    const v = this._voiceFor(2, 0.22, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    Lreset();
    L.buf = this._pink; L.rate = 1.4; L.type = 'bandpass';
    L.f0 = 2200; L.f1 = 3800; L.q = 0.55;
    L.peak = 0.16 * s; L.attack = 0.010; L.decay = 0.11;
    this._spawnNoise(v, w);
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 5400; L.q = 3.5;
    L.peak = 0.09 * s; L.attack = 0.004; L.decay = 0.07; L.delay = 0.03;
    this._spawnNoise(v, w);
    return v;
  }

  // =========================================================================
  // feedback do jogador
  // =========================================================================

  _playHitmarker(variant) {
    const kill = variant === 'kill';
    const head = variant === 'head';
    const v = this._voiceFor(9, kill ? 0.22 : 0.10, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;

    if (kill) {
      // mais grave e encorpado: le-se como "abateu"
      Lreset();
      L.wave = 'square'; L.f0 = 880; L.f1 = 460;
      L.type = 'lowpass'; L.cut = 2600; L.q = 0.8;
      L.peak = 0.28; L.attack = 0.0006; L.decay = 0.085;
      this._spawnTone(v, w);
      Lreset();
      L.buf = this._white; L.type = 'bandpass';
      L.f0 = 1500; L.f1 = 800; L.q = 3;
      L.peak = 0.26; L.attack = 0.0005; L.decay = 0.075;
      this._spawnNoise(v, w);
      return v;
    }

    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = head ? 4200 : 2600; L.q = head ? 5 : 3.2;
    L.peak = head ? 0.30 : 0.24; L.attack = 0.0004; L.decay = head ? 0.018 : 0.022;
    this._spawnNoise(v, w);
    Lreset();
    L.wave = 'square'; L.f0 = head ? 2600 : 1700; L.f1 = head ? 2200 : 1500;
    L.type = 'bandpass'; L.cut = head ? 2600 : 1700; L.q = 4;
    L.peak = 0.16; L.attack = 0.0004; L.decay = 0.020;
    this._spawnTone(v, w);
    if (head) {
      // segundo tique: assinatura de tiro na cabeca
      Lreset();
      L.buf = this._white; L.type = 'bandpass';
      L.f0 = 5200; L.q = 6; L.peak = 0.22; L.attack = 0.0004; L.decay = 0.016; L.delay = 0.045;
      this._spawnNoise(v, w);
    }
    return v;
  }

  _playPlayerVoice(variant) {
    if (variant === 'death') {
      const v = this._voiceFor(10, 1.8, this.sfxBus);
      if (!v) return null;
      const w = this._pWhen;
      v.send.gain.value = 0.55;
      Lreset();
      L.wave = 'sine'; L.f0 = 130; L.f1 = 34;
      L.type = 'lowpass'; L.cut = 400; L.q = 0.8;
      L.peak = 0.62; L.attack = 0.004; L.decay = 1.30;
      this._spawnTone(v, w);
      Lreset();
      L.buf = this._brown; L.rate = 0.7; L.type = 'lowpass';
      L.f0 = 900; L.f1 = 150; L.q = 0.8;
      L.peak = 0.40; L.attack = 0.006; L.decay = 1.10;
      this._spawnNoise(v, w);
      // zumbido de ouvido que fica depois do baque
      Lreset();
      L.wave = 'sine'; L.f0 = 3200; L.f1 = 2600;
      L.type = 'none'; L.cut = 0;
      L.peak = 0.055; L.attack = 0.05; L.decay = 1.60;
      this._spawnTone(v, w);
      this._hitMuffle = 700;
      return v;
    }

    // dano recebido: baque no peito + arranhao seco
    const v = this._voiceFor(10, 0.42, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    v.send.gain.value = 0.18;
    Lreset();
    L.wave = 'sine'; L.f0 = 165; L.f1 = 62;
    L.type = 'lowpass'; L.cut = 460; L.q = 0.8;
    L.peak = 0.52 * this._pVol; L.attack = 0.002; L.decay = 0.22;
    this._spawnTone(v, w);
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 1400; L.f1 = 500; L.q = 1.2;
    L.peak = 0.34 * this._pVol; L.attack = 0.001; L.decay = 0.12;
    L.drive = 5;
    this._spawnNoise(v, w);
    Lreset();
    L.buf = this._white; L.type = 'highpass';
    L.f0 = 5200; L.q = 0.7;
    L.peak = 0.12 * this._pVol; L.attack = 0.001; L.decay = 0.06;
    this._spawnNoise(v, w);
    return v;
  }

  /** Corpo caindo no chao (morte de bot). */
  _playBodyFall() {
    this._pRef = 5;
    this._pRoll = 1.6;
    const v = this._voiceFor(5, 0.6, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    v.send.gain.value = 0.24;
    Lreset();
    L.wave = 'sine'; L.f0 = 92; L.f1 = 48;
    L.type = 'lowpass'; L.cut = 260; L.q = 0.7;
    L.peak = 0.55; L.attack = 0.003; L.decay = 0.20;
    this._spawnTone(v, w);
    Lreset();
    L.buf = this._pink; L.rate = 0.8; L.type = 'lowpass';
    L.f0 = 1200; L.f1 = 350; L.q = 0.8;
    L.peak = 0.30; L.attack = 0.004; L.decay = 0.26;
    this._spawnNoise(v, w);
    // equipamento batendo no chao logo depois
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = 3400; L.q = 5;
    L.peak = 0.16; L.attack = 0.002; L.decay = 0.13; L.delay = 0.09;
    this._spawnNoise(v, w);
    Lreset();
    L.wave = 'sine'; L.f0 = 74; L.f1 = 44;
    L.type = 'lowpass'; L.cut = 200; L.q = 0.7;
    L.peak = 0.26; L.attack = 0.004; L.decay = 0.16; L.delay = 0.13;
    this._spawnTone(v, w);
    return v;
  }

  _playExplosion() {
    this._pRef = 12;
    this._pRoll = 0.8;
    const v = this._voiceFor(10, 1.6, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    v.send.gain.value = 0.75;
    Lreset();
    L.buf = this._white; L.type = 'lowpass';
    L.f0 = 2200; L.f1 = 90; L.q = 0.9;
    L.peak = 0.95; L.attack = 0.002; L.decay = 0.55; L.drive = 7;
    this._spawnNoise(v, w);
    Lreset();
    L.wave = 'sine'; L.f0 = 120; L.f1 = 26;
    L.type = 'lowpass'; L.cut = 300; L.q = 0.8;
    L.peak = 0.85; L.attack = 0.002; L.decay = 0.75;
    this._spawnTone(v, w);
    Lreset();
    L.buf = this._pink; L.rate = 0.75; L.type = 'bandpass';
    L.f0 = 600; L.f1 = 220; L.q = 0.6;
    L.peak = 0.40; L.attack = 0.05; L.decay = 1.30;
    this._spawnNoise(v, w);
    this._duck(w, 0.45, 0.14);
    this._hitMuffle = Math.min(this._hitMuffle, 1400);
    return v;
  }

  // =========================================================================
  // avisos de jogo e interface
  // =========================================================================

  _playWaveSting(variant) {
    const clear = variant === 'clear';
    const v = this._voiceFor(9, clear ? 1.5 : 1.9, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    v.send.gain.value = 0.6;

    if (clear) {
      // duas notas subindo: alivio, nao fanfarra
      Lreset();
      L.wave = 'sine'; L.f0 = 392; L.type = 'lowpass'; L.cut = 2600; L.q = 0.8;
      L.peak = 0.22; L.attack = 0.008; L.decay = 0.45;
      this._spawnTone(v, w);
      Lreset();
      L.wave = 'sine'; L.f0 = 587; L.type = 'lowpass'; L.cut = 3200; L.q = 0.8;
      L.peak = 0.20; L.attack = 0.008; L.decay = 0.75; L.delay = 0.16;
      this._spawnTone(v, w);
      Lreset();
      L.wave = 'triangle'; L.f0 = 196; L.type = 'lowpass'; L.cut = 900; L.q = 0.7;
      L.peak = 0.16; L.attack = 0.02; L.decay = 0.9;
      this._spawnTone(v, w);
      return v;
    }

    // inicio de onda: dois graves desafinados = tensao
    Lreset();
    L.wave = 'sawtooth'; L.f0 = 55; L.f1 = 52;
    L.type = 'lowpass'; L.cut = 420; L.q = 3;
    L.peak = 0.34; L.attack = 0.05; L.decay = 1.20;
    this._spawnTone(v, w);
    Lreset();
    L.wave = 'sawtooth'; L.f0 = 55.6; L.f1 = 51.4;
    L.type = 'lowpass'; L.cut = 380; L.q = 3;
    L.peak = 0.30; L.attack = 0.07; L.decay = 1.30;
    this._spawnTone(v, w);
    Lreset();
    L.buf = this._brown; L.rate = 0.6; L.type = 'lowpass';
    L.f0 = 260; L.f1 = 90; L.q = 0.8;
    L.peak = 0.30; L.attack = 0.10; L.decay = 1.40;
    this._spawnNoise(v, w);
    return v;
  }

  _playGameSting(variant) {
    const win = variant === 'win';
    const v = this._voiceFor(10, win ? 2.4 : 2.6, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    v.send.gain.value = 0.7;

    if (win) {
      const notes = [261.6, 392.0, 523.3];
      for (let i = 0; i < notes.length; i++) {
        Lreset();
        L.wave = 'sine'; L.f0 = notes[i];
        L.type = 'lowpass'; L.cut = 3200; L.q = 0.8;
        L.peak = 0.22; L.attack = 0.010; L.decay = 0.9 + i * 0.35; L.delay = i * 0.17;
        this._spawnTone(v, w);
        Lreset();
        L.wave = 'triangle'; L.f0 = notes[i] * 0.5;
        L.type = 'lowpass'; L.cut = 1200; L.q = 0.7;
        L.peak = 0.12; L.attack = 0.015; L.decay = 0.8 + i * 0.3; L.delay = i * 0.17;
        this._spawnTone(v, w);
      }
      return v;
    }

    // derrota: queda de tom com cauda longa e escura
    Lreset();
    L.wave = 'sawtooth'; L.f0 = 174; L.f1 = 58;
    L.type = 'lowpass'; L.cut = 700; L.q = 2.2;
    L.peak = 0.32; L.attack = 0.03; L.decay = 1.9;
    this._spawnTone(v, w);
    Lreset();
    L.wave = 'sine'; L.f0 = 87; L.f1 = 41;
    L.type = 'lowpass'; L.cut = 300; L.q = 0.8;
    L.peak = 0.34; L.attack = 0.05; L.decay = 2.1;
    this._spawnTone(v, w);
    Lreset();
    L.buf = this._brown; L.rate = 0.5; L.type = 'lowpass';
    L.f0 = 300; L.f1 = 80; L.q = 0.8;
    L.peak = 0.26; L.attack = 0.20; L.decay = 2.0;
    this._spawnNoise(v, w);
    return v;
  }

  _playUi(variant) {
    const v = this._voiceFor(9, 0.10, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    const hover = variant === 'hover';
    Lreset();
    L.wave = 'square'; L.f0 = hover ? 1400 : 900;
    L.type = 'bandpass'; L.cut = hover ? 1400 : 900; L.q = 3;
    L.peak = hover ? 0.07 : 0.13; L.attack = 0.0006; L.decay = hover ? 0.020 : 0.035;
    this._spawnTone(v, w);
    Lreset();
    L.buf = this._white; L.type = 'bandpass';
    L.f0 = hover ? 5200 : 3200; L.q = 6;
    L.peak = hover ? 0.05 : 0.10; L.attack = 0.0005; L.decay = 0.015;
    this._spawnNoise(v, w);
    return v;
  }

  // =========================================================================
  // ambiente de rua
  // =========================================================================

  _startAmbience() {
    const ac = this._ac;
    if (this._ambienceNodes) return;
    try {
      const out = this.ambientBus;

      // vento: ruido rosa numa banda larga que oscila devagar
      const windSrc = ac.createBufferSource();
      windSrc.buffer = this._pink;
      windSrc.loop = true;
      windSrc.playbackRate.value = 0.35;
      const windBp = ac.createBiquadFilter();
      windBp.type = 'bandpass';
      windBp.frequency.value = 420;
      windBp.Q.value = 0.55;
      const windGain = ac.createGain();
      windGain.gain.value = 0.42;
      windSrc.connect(windBp); windBp.connect(windGain); windGain.connect(out);

      const lfoF = ac.createOscillator();
      lfoF.type = 'sine'; lfoF.frequency.value = 0.071;
      const lfoFAmt = ac.createGain(); lfoFAmt.gain.value = 190;
      lfoF.connect(lfoFAmt); lfoFAmt.connect(windBp.frequency);

      const lfoG = ac.createOscillator();
      lfoG.type = 'sine'; lfoG.frequency.value = 0.043;
      const lfoGAmt = ac.createGain(); lfoGAmt.gain.value = 0.20;
      lfoG.connect(lfoGAmt); lfoGAmt.connect(windGain.gain);

      // rumor grave da cidade ao longe
      const rumSrc = ac.createBufferSource();
      rumSrc.buffer = this._brown;
      rumSrc.loop = true;
      rumSrc.playbackRate.value = 0.5;
      const rumLp = ac.createBiquadFilter();
      rumLp.type = 'lowpass'; rumLp.frequency.value = 115; rumLp.Q.value = 0.6;
      const rumGain = ac.createGain(); rumGain.gain.value = 0.55;
      rumSrc.connect(rumLp); rumLp.connect(rumGain); rumGain.connect(out);

      const lfoR = ac.createOscillator();
      lfoR.type = 'sine'; lfoR.frequency.value = 0.027;
      const lfoRAmt = ac.createGain(); lfoRAmt.gain.value = 0.22;
      lfoR.connect(lfoRAmt); lfoRAmt.connect(rumGain.gain);

      // chiado de ar bem fino: preenche o silencio sem chamar atencao
      const airSrc = ac.createBufferSource();
      airSrc.buffer = this._white;
      airSrc.loop = true;
      airSrc.playbackRate.value = 0.25;
      const airHp = ac.createBiquadFilter();
      airHp.type = 'highpass'; airHp.frequency.value = 5200; airHp.Q.value = 0.5;
      const airGain = ac.createGain(); airGain.gain.value = 0.05;
      airSrc.connect(airHp); airHp.connect(airGain); airGain.connect(out);

      const t = ac.currentTime + 0.02;
      windSrc.start(t, Math.random());
      rumSrc.start(t, Math.random());
      airSrc.start(t, Math.random());
      lfoF.start(t); lfoG.start(t); lfoR.start(t);

      this._ambienceNodes = [windSrc, windBp, windGain, lfoF, lfoFAmt, lfoG, lfoGAmt,
        rumSrc, rumLp, rumGain, lfoR, lfoRAmt, airSrc, airHp, airGain];
    } catch (err) {
      this._warn(err);
    }
  }

  _setAmbience(level) {
    this._ambientTarget = level;
  }

  _updateAmbience(dt, now) {
    // interpolacao em JS (sem agendar evento por frame no AudioParam)
    const target = this._ambientTarget * (this._ambientGainScale ?? 0.5);
    const k = Math.min(1, dt * 1.8);
    this._ambientNow += (target - this._ambientNow) * k;
    if (this.ambientBus) this.ambientBus.gain.value = this._ambientNow;

    if (!this._started || this._ambientTarget <= 0.01) return;
    if (now < this._nextAmbientAt) return;
    this._nextAmbientAt = now + 9 + Math.random() * 17;

    // evento distante ocasional, sempre longe e baixo: da vida sem cansar
    const ang = Math.random() * Math.PI * 2;
    const r = 45 + Math.random() * 30;
    const lx = this._listenerPos.x + Math.cos(ang) * r;
    const lz = this._listenerPos.z + Math.sin(ang) * r;
    const ly = this._listenerPos.y + (Math.random() - 0.3) * 8;
    const kind = Math.random();
    const name = kind < 0.45 ? 'ambient.gust' : (kind < 0.8 ? 'ambient.rumble' : 'ambient.gun');
    this._schedule(name, 0.01, 1, 1, true, lx, ly, lz);
  }

  _playAmbientEvent(variant) {
    this._pRef = 25;
    this._pRoll = 0.6;
    if (variant === 'gun') {
      // tiroteio distante, bem abafado: sugere que a operacao e maior que o mapa
      const v = this._voiceFor(1, 1.5, this.sfxBus);
      if (!v) return null;
      const w = this._pWhen;
      v.send.gain.value = 0.7;
      v.lp.frequency.value = Math.min(v.lp.frequency.value, 620);
      const shots = 2 + Math.floor(Math.random() * 3);
      for (let i = 0; i < shots; i++) {
        Lreset();
        L.buf = this._white; L.type = 'lowpass';
        L.f0 = 700; L.f1 = 130; L.q = 0.8;
        L.peak = 0.30 + Math.random() * 0.10;
        L.attack = 0.004; L.decay = 0.16;
        L.delay = i * (0.09 + Math.random() * 0.06);
        this._spawnNoise(v, w);
      }
      return v;
    }
    if (variant === 'rumble') {
      const v = this._voiceFor(1, 2.6, this.sfxBus);
      if (!v) return null;
      const w = this._pWhen;
      v.send.gain.value = 0.45;
      Lreset();
      L.buf = this._brown; L.rate = 0.45; L.type = 'lowpass';
      L.f0 = 190; L.f1 = 80; L.q = 0.7;
      L.peak = 0.40; L.attack = 0.7; L.decay = 1.7;
      this._spawnNoise(v, w);
      return v;
    }
    // rajada de vento
    const v = this._voiceFor(1, 3.0, this.sfxBus);
    if (!v) return null;
    const w = this._pWhen;
    v.send.gain.value = 0.35;
    Lreset();
    L.buf = this._pink; L.rate = 0.5; L.type = 'bandpass';
    L.f0 = 380; L.f1 = 900; L.q = 0.5;
    L.peak = 0.34; L.attack = 0.9; L.decay = 1.9;
    this._spawnNoise(v, w);
    return v;
  }

  // =========================================================================
  // vida critica: zumbido de pressao + abafamento
  // =========================================================================

  _ensureHum() {
    if (this._humNodes || !this._ac) return;
    const ac = this._ac;
    try {
      const g = ac.createGain();
      g.gain.value = 0;
      g.connect(this.masterFilter);

      const t1 = ac.createOscillator();
      t1.type = 'sine'; t1.frequency.value = 3150;
      const t1g = ac.createGain(); t1g.gain.value = 0.030;
      t1.connect(t1g); t1g.connect(g);

      const t2 = ac.createOscillator();
      t2.type = 'sine'; t2.frequency.value = 4720;
      const t2g = ac.createGain(); t2g.gain.value = 0.016;
      t2.connect(t2g); t2g.connect(g);

      const n = ac.createBufferSource();
      n.buffer = this._brown; n.loop = true; n.playbackRate.value = 0.4;
      const nlp = ac.createBiquadFilter();
      nlp.type = 'lowpass'; nlp.frequency.value = 95; nlp.Q.value = 0.8;
      const ng = ac.createGain(); ng.gain.value = 0.55;
      n.connect(nlp); nlp.connect(ng); ng.connect(g);

      // batimento lento no zumbido: soa organico, nao um bipe de teste
      const lfo = ac.createOscillator();
      lfo.type = 'sine'; lfo.frequency.value = 0.35;
      const lfoAmt = ac.createGain(); lfoAmt.gain.value = 0.006;
      lfo.connect(lfoAmt); lfoAmt.connect(t1g.gain);

      const t = ac.currentTime + 0.02;
      t1.start(t); t2.start(t); n.start(t, Math.random()); lfo.start(t);

      this._humGain = g;
      this._humNodes = [t1, t1g, t2, t2g, n, nlp, ng, lfo, lfoAmt, g];
    } catch (err) {
      this._warn(err);
    }
  }

  _updateHealthAudio(dt) {
    const player = this.ctx.player;
    const maxHp = (player && player.maxHealth) || PLAYER.MAX_HEALTH;
    const hp = player && typeof player.health === 'number' ? player.health : maxHp;
    const alive = player ? player.alive !== false : true;
    const frac = Math.max(0, Math.min(1, hp / Math.max(1, maxHp)));

    // intensidade cresce so abaixo de 35% da vida
    let t = frac < 0.35 ? (0.35 - frac) / 0.35 : 0;
    if (!alive) t = 1;
    if (this._state !== 'playing') t = 0;

    if (t > 0.001) this._ensureHum();
    this._humLevel += (t - this._humLevel) * Math.min(1, dt * 2.2);
    if (this._humGain) this._humGain.gain.value = this._humLevel * 0.9;

    // abafamento: por vida baixa (continuo) e por dano/explosao (temporario)
    const healthMuffle = 22000 - this._humLevel * this._humLevel * 19000;
    this._hitMuffle += (22000 - this._hitMuffle) * Math.min(1, dt * 1.4);
    let target = Math.min(healthMuffle, this._hitMuffle);
    if (this._state === 'paused') target = Math.min(target, 1100);
    if (this._state === 'gameover') target = Math.min(target, 2600);

    this._muffle += (target - this._muffle) * Math.min(1, dt * 6);
    if (this.masterFilter) {
      this.masterFilter.frequency.value = Math.max(200, Math.min(22000, this._muffle));
    }
  }

  // =========================================================================
  // agenda (sons futuros sem segurar voz antes da hora)
  // =========================================================================

  _schedule(name, delay, volume, pitch, positional, x, y, z) {
    if (!this._ac) return;
    for (let i = 0; i < this._sched.length; i++) {
      const s = this._sched[i];
      if (s.active) continue;
      s.active = true;
      s.at = this._ac.currentTime + Math.max(0, delay);
      s.name = name;
      s.volume = volume;
      s.pitch = pitch;
      s.positional = positional;
      s.x = x; s.y = y; s.z = z;
      return;
    }
    // agenda cheia: descartar e correto (nunca crescer array no caminho quente)
  }

  _runScheduler(now) {
    const o = this._o;
    for (let i = 0; i < this._sched.length; i++) {
      const s = this._sched[i];
      if (!s.active || now < s.at) continue;
      s.active = false;
      this._resetOpts();
      o.volume = s.volume;
      o.pitch = s.pitch;
      if (s.positional) { _tmpA.set(s.x, s.y, s.z); o.position = _tmpA; }
      this.play(s.name, o);
    }
  }

  // =========================================================================
  // eventos do jogo
  // =========================================================================

  _resetOpts() {
    const o = this._o;
    o.position = null; o.volume = 1; o.pitch = 1;
    o.far = false; o.running = false; o.crouch = false;
    o.duration = 0; o.empty = false;
    return o;
  }

  _bindEvents() {
    const E = this.ctx.EVENTS;
    const bus = this.ctx.events;
    const o = this._o;

    bus.on(E.GAME_STATE, (p) => {
      this.resume();
      const st = p?.state || 'menu';
      this._state = st;
      if (st === 'playing') this._setAmbience(1.0);
      else if (st === 'menu') this._setAmbience(0.35);
      else if (st === 'paused') this._setAmbience(0.25);
      else if (st === 'victory') { this._setAmbience(0.5); this._resetOpts(); this.play('game.win', o); }
      else if (st === 'gameover') { this._setAmbience(0.4); this._resetOpts(); this.play('game.over', o); }
    });

    bus.on(E.WEAPON_FIRE, (p) => {
      this._resetOpts();
      this._shotName(this._familyOf(p?.weapon));
      this.play(this._nameBuf, o);
    });

    bus.on(E.WEAPON_DRY, () => {
      this._resetOpts();
      this.play('weapon.dry', o);
    });

    bus.on(E.WEAPON_RELOAD_START, (p) => {
      this._resetOpts();
      o.duration = typeof p?.duration === 'number' ? p.duration : 2.2;
      // recarga com pente vazio tem o passo extra do ferrolho
      o.empty = p?.empty === true || this.ctx.weapons?.ammo === 0;
      this.play('reload', o);
    });

    bus.on(E.WEAPON_SWITCH, () => {
      this._resetOpts();
      this.play('weapon.switch', o);
    });

    bus.on(E.WEAPON_ADS, (p) => {
      if (!p || p.active === undefined || p.active) {
        this._resetOpts();
        this.play('weapon.ads', o);
      }
    });

    bus.on(E.BULLET_IMPACT, (p) => {
      if (!p) return;
      this._resetOpts();
      o.position = p.point || null;
      o.volume = 1;
      this._impactName(p.surface);
      this.play(this._nameBuf, o);
    });

    bus.on(E.ENTITY_DAMAGED, (p) => {
      if (!p) return;
      this._resetOpts();
      o.position = p.point || null;
      o.volume = p.part === 'head' ? 1.15 : 1;
      this.play('impact.flesh', o);
    });

    bus.on(E.ENTITY_KILLED, (p) => {
      this._resetOpts();
      const ent = p?.entity;
      const pos = ent?.position || ent?.mesh?.position || null;
      if (pos) o.position = pos;
      this.play('body', o);
    });

    bus.on(E.PLAYER_DAMAGED, (p) => {
      this._resetOpts();
      const amt = typeof p?.amount === 'number' ? p.amount : 10;
      o.volume = Math.max(0.5, Math.min(1.5, 0.6 + amt / 45));
      this.play('player.hurt', o);
      // pancada forte abafa o mundo por um instante
      if (amt > 14) this._hitMuffle = Math.min(this._hitMuffle, 2400 - Math.min(1200, amt * 20));
    });

    bus.on(E.PLAYER_DIED, () => {
      this._resetOpts();
      this.play('player.death', o);
    });

    bus.on(E.FOOTSTEP, (p) => {
      if (!p) return;
      this._resetOpts();
      const isPlayer = p.isPlayer !== false;
      if (!isPlayer && p.position) o.position = p.position;
      o.running = !!p.running;
      o.crouch = !!(this.ctx.controller?.crouching && isPlayer);
      o.volume = isPlayer ? 0.85 : 1.0;
      this._stepName(p.surface);
      this.play(this._nameBuf, o);
    });

    bus.on(E.PLAYER_JUMP, () => {
      this._resetOpts();
      this.play('jump', o);
    });

    bus.on(E.PLAYER_LAND, (p) => {
      this._resetOpts();
      o.volume = typeof p?.impact === 'number' ? Math.max(0.35, Math.min(2.0, p.impact)) : 1;
      this._landName(p?.surface);
      this.play(this._nameBuf, o);
    });

    bus.on(E.HITMARKER, (p) => {
      this._resetOpts();
      this.play(p?.killed ? 'hitmarker.kill' : (p?.headshot ? 'hitmarker.head' : 'hitmarker'), o);
    });

    bus.on(E.WAVE_START, () => { this._resetOpts(); this.play('wave.start', o); });
    bus.on(E.WAVE_CLEAR, () => { this._resetOpts(); this.play('wave.clear', o); });

    bus.on(E.AI_FIRE, (p) => {
      if (!p) return;
      this._resetOpts();
      o.position = p.origin || p.bot?.position || null;
      o.far = true;
      this._shotName(this._familyOf(p.bot?.weapon));
      this.play(this._nameBuf, o);
      this._maybeWhizz(p.origin, p.dir);
    });

    bus.on(E.AI_ALERT, (p) => {
      const pos = p?.position || p?.bot?.position;
      if (!pos) return;
      this._resetOpts();
      o.position = pos;
      o.volume = 0.8;
      this.play('gear', o);
    });

    bus.on(E.SFX, (p) => {
      if (!p?.name) return;
      this._resetOpts();
      o.position = p.position || null;
      o.volume = typeof p.volume === 'number' ? p.volume : 1;
      o.pitch = typeof p.pitch === 'number' ? p.pitch : 1;
      this.play(p.name, o);
    });
  }

  /** Gesto do usuario destrava o AudioContext (exigencia do navegador). */
  _bindGesture() {
    const types = ['pointerdown', 'mousedown', 'keydown', 'touchstart'];
    const fn = () => { this.resume(); };
    for (const t of types) window.addEventListener(t, fn, { passive: true });
    this._gestureOff = () => {
      for (const t of types) window.removeEventListener(t, fn);
    };
  }

  /**
   * Bala passando perto: se a trajetoria do bot cruza a menos de ~2,2 m do
   * ouvido, toca o assobio no ponto mais proximo, com o atraso certo
   * (a bala chega antes do som do tiro).
   */
  _maybeWhizz(origin, dir) {
    if (!origin || !dir) return;
    _tmpA.set(origin.x ?? origin[0] ?? 0, origin.y ?? origin[1] ?? 0, origin.z ?? origin[2] ?? 0);
    _tmpB.set(dir.x ?? dir[0] ?? 0, dir.y ?? dir[1] ?? 0, dir.z ?? dir[2] ?? 0);
    const len = _tmpB.length();
    if (len < 1e-4) return;
    _tmpB.multiplyScalar(1 / len);

    const ox = this._listenerPos.x - _tmpA.x;
    const oy = this._listenerPos.y - _tmpA.y;
    const oz = this._listenerPos.z - _tmpA.z;
    const t = ox * _tmpB.x + oy * _tmpB.y + oz * _tmpB.z;
    if (t < 2 || t > 220) return;

    const cx = _tmpA.x + _tmpB.x * t;
    const cy = _tmpA.y + _tmpB.y * t;
    const cz = _tmpA.z + _tmpB.z * t;
    const dx = this._listenerPos.x - cx;
    const dy = this._listenerPos.y - cy;
    const dz = this._listenerPos.z - cz;
    const perp = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (perp > 2.2 || perp < 0.12) return;

    const delay = Math.min(0.35, t / BULLET_SPEED);
    const vol = 0.9 * (1 - perp / 2.2) + 0.25;
    this._schedule('whizz', delay, vol, 0.9 + Math.random() * 0.3, true, cx, cy, cz);
  }

  // =========================================================================
  // utilitarios
  // =========================================================================

  /**
   * Monta o nome do som sem concatenar string nova a cada evento quando o
   * resultado nao mudou (tiro em rajada usa sempre o mesmo nome).
   */
  _shotName(family) {
    if (this._lastFamily !== family) {
      this._lastFamily = family;
      this._lastShotName = `shot.${family}`;
    }
    this._nameBuf = this._lastShotName;
  }

  _impactName(surface) {
    const s = this._surfaceOf(surface);
    if (this._lastImpactSurface !== s) {
      this._lastImpactSurface = s;
      this._lastImpactName = `impact.${s}`;
    }
    this._nameBuf = this._lastImpactName;
  }

  _stepName(surface) {
    const s = this._surfaceOf(surface);
    if (this._lastStepSurface !== s) {
      this._lastStepSurface = s;
      this._lastStepName = `footstep.${s}`;
    }
    this._nameBuf = this._lastStepName;
  }

  _landName(surface) {
    const s = this._surfaceOf(surface);
    if (this._lastLandSurface !== s) {
      this._lastLandSurface = s;
      this._lastLandName = `land.${s}`;
    }
    this._nameBuf = this._lastLandName;
  }

  /** Normaliza para um nome valido de SURFACES (fallback: concrete). */
  _surfaceOf(s) {
    if (typeof s === 'string' && SURFACES.indexOf(s) >= 0) return s;
    return 'concrete';
  }

  /**
   * Descobre a familia de timbre a partir do que a arma expuser. Primeiro pelo
   * texto (sound/id/name/type), depois por numeros (pellets, rpm, automatico).
   */
  _familyOf(weapon) {
    if (!weapon || typeof weapon !== 'object') return 'rifle';
    const cached = this._weaponFamily.get(weapon);
    if (cached) return cached;

    let s = '';
    try {
      s = String(weapon.sound || weapon.id || weapon.key || weapon.name || weapon.type || '').toLowerCase();
    } catch { s = ''; }

    let fam = '';
    if (/(smg|sub|mp5|mp7|uzi|vector|p90)/.test(s)) fam = 'smg';
    else if (/(shotgun|espingarda|pump|buck|calibre)/.test(s)) fam = 'shotgun';
    else if (/(dmr|sniper|marksman|precis|scout|designated|semi)/.test(s)) fam = 'dmr';
    else if (/(rifle|fuzil|assalto|carbine|ak|m4)/.test(s)) fam = 'rifle';

    if (!fam) {
      const pellets = Number(weapon.pellets) || 0;
      const rpm = Number(weapon.rpm) || 0;
      if (pellets > 1) fam = 'shotgun';
      else if (rpm >= 850) fam = 'smg';
      else if (rpm > 0 && rpm < 420) fam = 'dmr';
      else fam = 'rifle';
    }

    this._weaponFamily.set(weapon, fam);
    return fam;
  }

  /** Degrada em silencio: avisa uma unica vez e nunca derruba o frame. */
  _warn(err) {
    if (this._warned) return;
    this._warned = true;
    console.warn('[AudioSystem] degradado:', err);
  }
}
